/**
 * سیاست حریم خصوصی
 * ==================
 * صفحه‌ای عمومی و بدون نیاز به ورود. دو مصرف دارد:
 *
 * ۱. کافه‌بازار برای اپی که اطلاعات هویتی می‌گیرد، نشانی این صفحه را
 *    الزامی می‌داند و بدونش اپ را منتشر نمی‌کند.
 * ۲. خود اپ اندروید از «بیشتر» به همین نشانی لینک می‌دهد.
 *
 * ── چرا فهرست داده‌ها این‌قدر مشخص است ────────────────────────────────
 * متن‌های کلیِ «ما به حریم خصوصی شما اهمیت می‌دهیم» نه به کاربر چیزی
 * می‌گویند و نه بازبینِ فروشگاه را قانع می‌کنند. اینجا دقیقاً همان
 * چیزهایی فهرست شده که سرور واقعاً ذخیره می‌کند — نه بیشتر، نه کمتر.
 */
import { ShieldCheck, CreditCard, Share2, Clock, Users, Mail } from 'lucide-react'
import { Shell } from '../components/Layout'
import { Card, SectionHead } from '../components/UI'
import { useSeo } from '../lib/seo'

const COLLECTED: Array<[string, string]> = [
  ['شماره موبایل', 'برای ساخت حساب، ورود با رمز یکبارمصرف و اطلاع‌رسانی تراکنش‌ها.'],
  ['نام و نام خانوادگی، کد ملی و تاریخ تولد', 'برای احراز هویت، مطابق الزامات قانونی حاکم بر خدمات مالی.'],
  ['تصویر مدارک هویتی و تصویر چهره', 'تنها در فرآیند احراز هویت و برای تطبیق هویت صاحب حساب.'],
  ['شماره کارت و شبای بانکی', 'برای واریز و برداشت ریالی. برداشت فقط به نام صاحب حساب انجام می‌شود.'],
  ['آدرس کیف پول رمزارزی', 'برای واریز و برداشت رمزارز.'],
  ['سوابق سفارش و تراکنش', 'برای نمایش تاریخچه، صدور فاکتور و رسیدگی به اختلاف‌ها.'],
  ['نشانی IP و اطلاعات دستگاه', 'برای تشخیص ورود مشکوک و جلوگیری از سوءاستفاده از حساب.'],
]

const NOT_COLLECTED = [
  'اپ اندروید ارزینو به دوربین، مخاطبین، موقعیت مکانی، پیامک‌ها و فایل‌های شما دسترسی ندارد؛ تنها مجوزهای آن «اینترنت» و «وضعیت شبکه» است.',
  'هنگام بارگذاری مدارک، انتخاب فایل با انتخابگرِ خودِ اندروید انجام می‌شود و اپ فقط همان فایلی را می‌بیند که خودتان انتخاب کرده‌اید.',
  'هیچ ابزار تبلیغاتی یا ردیاب شخص ثالثی در اپ وجود ندارد و اپ از سرویس‌های گوگل استفاده نمی‌کند.',
  'ارزینو هرگز رمز عبور، کد دومرحله‌ای یا رمز پویای کارت شما را درخواست نمی‌کند.',
]

const SHARING = [
  'با مراجع قانونی، تنها در چارچوب قوانین جاری و در پاسخ به درخواست رسمی.',
  'با درگاه پرداخت و بانک طرف قرارداد، تنها آن مقدار از اطلاعات که برای انجام همان تراکنش لازم است.',
  'با سرویس‌دهندهٔ پیامک، تنها شماره موبایل و متن پیامکِ همان اطلاع‌رسانی.',
]

export default function Privacy() {
  useSeo({
    title: 'سیاست حریم خصوصی',
    description:
      'ارزینو چه اطلاعاتی از شما جمع‌آوری می‌کند، چرا، چه مدت نگه می‌دارد و با چه کسانی به اشتراک می‌گذارد.',
    keywords: 'حریم خصوصی، سیاست حفظ اطلاعات، امنیت اطلاعات کاربران',
    canonical: '/privacy',
  }, [])

  return (
    <Shell>
      <SectionHead
        title="سیاست حریم خصوصی"
        subtitle="چه چیزی جمع می‌کنیم، چرا، چقدر نگه می‌داریم و با چه کسی به اشتراک می‌گذاریم"
      />

      <Card className="mb-5">
        <p className="max-w-3xl text-sm leading-9 text-slate-300">
          این سیاست بر وب‌سایت ارزینو و اپلیکیشن اندروید آن حاکم است. با ساخت حساب کاربری،
          استفاده شما از خدمات ارزینو مشمول همین سیاست می‌شود. هر تغییری در آن، پیش از
          اجرا در همین صفحه منتشر می‌شود.
        </p>
      </Card>

      {/* ── چه چیزی جمع می‌شود */}
      <Card className="mb-5">
        <h3 className="mb-4 flex items-center gap-2 font-bold text-white">
          <CreditCard className="h-4 w-4 text-brand-300" /> اطلاعاتی که جمع‌آوری می‌کنیم
        </h3>
        <div className="space-y-4">
          {COLLECTED.map(([what, why]) => (
            <div key={what} className="border-r-2 border-brand-500/30 pr-4">
              <div className="text-sm font-semibold text-white">{what}</div>
              <p className="mt-1 text-xs leading-7 text-slate-400">{why}</p>
            </div>
          ))}
        </div>
      </Card>

      {/* ── چه چیزی جمع نمی‌شود */}
      <Card className="mb-5">
        <h3 className="mb-4 flex items-center gap-2 font-bold text-white">
          <ShieldCheck className="h-4 w-4 text-up" /> اطلاعاتی که جمع‌آوری نمی‌کنیم
        </h3>
        <ul className="space-y-3 text-sm leading-8 text-slate-400">
          {NOT_COLLECTED.map((line) => <li key={line}>• {line}</li>)}
        </ul>
      </Card>

      <div className="grid gap-4 lg:grid-cols-2">
        {/* ── اشتراک‌گذاری */}
        <Card>
          <h3 className="mb-4 flex items-center gap-2 font-bold text-white">
            <Share2 className="h-4 w-4 text-brand-300" /> اشتراک‌گذاری با دیگران
          </h3>
          <p className="mb-3 text-xs leading-7 text-slate-400">
            اطلاعات شما فروخته یا اجاره داده نمی‌شود. تنها در این موارد در اختیار دیگری
            قرار می‌گیرد:
          </p>
          <ul className="space-y-3 text-sm leading-8 text-slate-400">
            {SHARING.map((line) => <li key={line}>• {line}</li>)}
          </ul>
        </Card>

        {/* ── نگهداری */}
        <Card>
          <h3 className="mb-4 flex items-center gap-2 font-bold text-white">
            <Clock className="h-4 w-4 text-brand-300" /> مدت نگهداری و امنیت
          </h3>
          <ul className="space-y-3 text-sm leading-8 text-slate-400">
            <li>
              • سوابق مالی و مدارک احراز هویت تا مدتی که قوانین مبارزه با پول‌شویی الزام
              می‌کنند نگهداری می‌شوند، حتی اگر حساب بسته شود.
            </li>
            <li>• ارتباط با سرور تنها روی HTTPS انجام می‌شود.</li>
            <li>• نشست ورود شما روی گوشی به‌صورت رمزشده ذخیره می‌شود، نه متن ساده.</li>
            <li>• دسترسی کارکنان به مدارک هویتی محدود و ثبت‌شده است.</li>
          </ul>
        </Card>
      </div>

      {/* ── حقوق کاربر */}
      <Card className="mt-5">
        <h3 className="mb-4 flex items-center gap-2 font-bold text-white">
          <Users className="h-4 w-4 text-brand-300" /> حقوق شما
        </h3>
        <ul className="space-y-3 text-sm leading-8 text-slate-400">
          <li>• می‌توانید اطلاعات حساب خود را در بخش «احراز هویت» ببینید و اصلاح کنید.</li>
          <li>
            • می‌توانید درخواست حذف حساب بدهید. اطلاعاتی که نگهداری‌شان قانوناً الزامی است
            باقی می‌ماند و بقیه حذف می‌شود.
          </li>
          <li>• می‌توانید اطلاع‌رسانی پیامکی را از بخش «بیشتر» خاموش کنید.</li>
          <li>• می‌توانید تاریخچهٔ معاملات و فاکتورهای خود را در هر زمان دریافت کنید.</li>
        </ul>
      </Card>

      <Card className="mt-5">
        <h3 className="mb-3 flex items-center gap-2 font-bold text-white">
          <Mail className="h-4 w-4 text-brand-300" /> تماس دربارهٔ حریم خصوصی
        </h3>
        <p className="text-sm leading-8 text-slate-400">
          برای هر پرسش یا درخواستی دربارهٔ اطلاعات شخصی‌تان، از بخش «پشتیبانی» در سایت یا
          اپ، گفتگویی با دپارتمان «پشتیبانی» باز کنید. پاسخ به درخواست‌های مربوط به
          داده‌های شخصی در اولویت است.
        </p>
      </Card>
    </Shell>
  )
}
